import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClerkToken } from '@/hooks/useClerkToken';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { PLAY_CLAW_JWT_TEMPLATE } from '@/lib/auth';
import {
  AttemptStartSchema,
  parseControlMessage,
  type ControlClientMessage,
  type ControlMessage,
} from '@/contracts';
import { getTraceId, logError, logInfo, logWarn } from '@/lib/logger';
import { getFullJitterBackoffDelayMs } from '@/lib/reconnectBackoff';
import {
  getBufferedAmountThresholdCrossings,
  getGamepadCommandKind,
  MOTION_DIAGNOSTIC_SAMPLE_RATE,
  shouldLogSample,
} from '@/lib/teleopMotionDiagnostics';
import { isVideoDebugEnabledFromLocation } from '@/lib/videoDebug';
import { addWsBreadcrumb } from '@/lib/wsErrorBreadcrumbs';
import { TELEOP_SETTINGS } from '@/app/Teleop/teleopConfig';

export type WebSocketProviderProps = {
  children: React.ReactNode;
  sessionId: string;
  robotId?: string | null;
  leaseToken?: string | null;
  enabled?: boolean;
  onInvalidLease?: () => void;
};

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const WS_DEBUG = import.meta.env.VITE_WS_DEBUG === 'true';
const WS_STATUS_LOG = import.meta.env.VITE_WS_STATUS_LOG === 'true';
const WS_VIDEO_DEBUG = isVideoDebugEnabledFromLocation();
const WS_CONNECT_DELAY_MS = 150;
const WS_RECONNECT_BASE_DELAY_MS = 500;
const WS_RECONNECT_MAX_DELAY_MS = 10000;
const WS_HEARTBEAT_INTERVAL_MS = 25000;
const MOTION_BUFFERED_AMOUNT_LIMIT_BYTES =
  TELEOP_SETTINGS.joystick.motionBufferedAmountLimitBytes;
const LEASE_RECOVERY_ERRORS = new Set([
  'Invalid lease',
  'Lease expired',
  'Lease/session mismatch',
  'Lease/robot mismatch',
  'Missing lease context',
]);

const resolveControlWsUrl = () => {
  const raw = import.meta.env.VITE_CONTROL_WS || API_BASE;
  let urlString = raw.trim().replace(/\/$/, '');
  if (!/^wss?:\/\//.test(urlString) && /^https?:\/\//.test(urlString)) {
    urlString = urlString.replace(/^http/, 'ws');
  }
  if (!/^wss?:\/\//.test(urlString)) {
    throw new Error(`VITE_CONTROL_WS must start with ws:// or wss:// (or http(s)://): ${raw}`);
  }
  const url = new URL(urlString);
  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/ws';
  } else if (!url.pathname.endsWith('/ws')) {
    url.pathname = url.pathname.replace(/\/$/, '') + '/ws';
  }
  return url.toString();
};

export const WebSocketProvider = ({
  children,
  sessionId,
  robotId,
  leaseToken,
  enabled = true,
  onInvalidLease,
}: WebSocketProviderProps) => {
  const socketRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef(new Set<(message: ControlMessage) => void>());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastMotionBufferedAmountBytesRef = useRef(0);
  const motionBackpressureSkipCountRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<ControlMessage | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const { getToken, isLoaded, isSignedIn } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);
  const traceId = useMemo(() => getTraceId(), []);

  const wsUrl = useMemo(() => resolveControlWsUrl(), []);

  useEffect(() => {
    setLastMessage(undefined);
    lastMotionBufferedAmountBytesRef.current = 0;
    motionBackpressureSkipCountRef.current = 0;
  }, [sessionId]);

  const logMotionBufferedAmountThresholds = useCallback(
    (
      previousBufferedAmountBytes: number,
      currentBufferedAmountBytes: number,
    ) => {
      const thresholdCrossings = getBufferedAmountThresholdCrossings(
        previousBufferedAmountBytes,
        currentBufferedAmountBytes
      );

      thresholdCrossings.forEach((thresholdCrossing) => {
        const isRising = thresholdCrossing.direction === 'up';
        const logBufferedAmount = isRising ? logWarn : logInfo;
        logBufferedAmount(
          'teleop.motion.buffer_threshold',
          'control socket bufferedAmount crossed threshold',
          {
            buffered_amount_bytes: currentBufferedAmountBytes,
            previous_buffered_amount_bytes: previousBufferedAmountBytes,
            threshold_bytes: thresholdCrossing.thresholdBytes,
            direction: thresholdCrossing.direction,
            session_id: sessionId,
            robot_id: robotId ?? null,
          }
        );
      });
    },
    [robotId, sessionId]
  );

  useEffect(() => {
    if (!enabled || !sessionId || !isLoaded || !isSignedIn) {
      setConnected(false);
      if (WS_DEBUG || WS_VIDEO_DEBUG) {
        logInfo('ws.connect.skip', 'skip connect', {
          enabled,
          sessionId: Boolean(sessionId),
          robotId: Boolean(robotId),
          leaseToken: Boolean(leaseToken),
          isLoaded,
          isSignedIn,
        });
      }
      return;
    }

    let canceled = false;
    let socket: WebSocket | null = null;
    let connectTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const clearHeartbeatInterval = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (canceled || reconnectTimeoutRef.current) {
        return;
      }
      const attempt = reconnectAttemptRef.current;
      const delay = getFullJitterBackoffDelayMs({
        attempt,
        baseDelayMs: WS_RECONNECT_BASE_DELAY_MS,
        maxDelayMs: WS_RECONNECT_MAX_DELAY_MS,
      });
      reconnectAttemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
      addWsBreadcrumb({ event: 'reconnect_scheduled', surface: 'control' });
      if (WS_DEBUG) {
        logInfo('ws.reconnect.scheduled', 'reconnect scheduled', { attempt, delay });
      }
    };

    const sendHeartbeat = () => {
      const activeSocket = socketRef.current;
      if (!activeSocket) {
        return;
      }
      if (activeSocket.readyState !== WebSocket.OPEN) {
        return;
      }
      const heartbeatMessage: ControlClientMessage = {
        type: 'ws_ping',
        trace_id: traceId,
      };
      activeSocket.send(JSON.stringify(heartbeatMessage));
      addWsBreadcrumb({ event: 'message_out', surface: 'control', direction: 'out', ws_type: heartbeatMessage.type });
    };

    const startHeartbeatInterval = () => {
      clearHeartbeatInterval();
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat();
      }, WS_HEARTBEAT_INTERVAL_MS);
    };

    const connect = () => {
      if (canceled) return;
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        return;
      }
      if (socketRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }
      addWsBreadcrumb({ event: 'connect', surface: 'control' });
      socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      if (WS_DEBUG) {
        logInfo('ws.connecting', 'connecting', { wsUrl });
      }

      socket.addEventListener('open', async () => {
        addWsBreadcrumb({ event: 'open', surface: 'control' });
        if (canceled) return;
        if (WS_DEBUG) {
          logInfo('ws.open', 'open');
        }
        try {
          const token = await getToken();
          if (canceled) return;
          if (!token) {
            addWsBreadcrumb({ event: 'error', surface: 'control' });
            setError('Missing auth token');
            logWarn('ws.auth.missing', 'missing auth token');
            socket?.close();
            return;
          }
          if (WS_DEBUG) {
            logInfo('ws.auth.sending', 'sending auth');
          }
          addWsBreadcrumb({ event: 'auth_send', surface: 'control' });
          socket?.send(
            JSON.stringify({
              type: 'auth',
              token,
              trace_id: traceId,
              session_id: sessionId || undefined,
              robot_id: robotId || undefined,
              lease_token: leaseToken || undefined,
            })
          );
        } catch (err) {
          addWsBreadcrumb({ event: 'error', surface: 'control' });
          setError('Failed to authenticate');
          logWarn('ws.auth.failed', 'auth failed', { error: String(err) });
          socket?.close();
        }
      });

      socket.addEventListener('message', (event) => {
        if (canceled) return;
        try {
          const raw = JSON.parse(event.data) as unknown;
          addWsBreadcrumb({ event: 'message_in', surface: 'control', direction: 'in' });
          const parsed = parseControlMessage(raw);
          if (!parsed.ok) {
            const rawPayload =
              typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
            const rawType = typeof raw;
            let attemptStartIssues: unknown;
            let decodedRaw: unknown;
            let decodedParseError: string | null = null;
            let decodedAttemptStartIssues: unknown;
            if (WS_DEBUG) {
              if (raw && typeof raw === 'object' && 'type' in raw && raw.type === 'attempt_start') {
                const attemptParsed = AttemptStartSchema.safeParse(raw);
                if (!attemptParsed.success) {
                  attemptStartIssues = attemptParsed.error.issues;
                }
              }
              if (typeof raw === 'string') {
                try {
                  decodedRaw = JSON.parse(raw);
                  if (decodedRaw && typeof decodedRaw === 'object' && 'type' in decodedRaw) {
                    if (decodedRaw.type === 'attempt_start') {
                      const decodedAttemptParsed = AttemptStartSchema.safeParse(decodedRaw);
                      if (!decodedAttemptParsed.success) {
                        decodedAttemptStartIssues = decodedAttemptParsed.error.issues;
                      }
                    }
                  }
                } catch (error) {
                  decodedParseError = String(error);
                }
              }
            }
            if (WS_DEBUG && typeof window !== 'undefined') {
              type DebugInvalidMessage = {
                raw: string;
                issues?: unknown;
                code?: string;
                context?: unknown;
                attemptStartIssues?: unknown;
                rawType?: string;
                decodedRaw?: unknown;
                decodedParseError?: string | null;
                decodedAttemptStartIssues?: unknown;
              };
              type DebugBuffer = {
                wsInvalidMessages: string[];
                wsInvalidMessageDetails: DebugInvalidMessage[];
              };
              const debugWindow = window as Window & { __DWELLBOT_DEBUG__?: DebugBuffer };
              const existing = debugWindow.__DWELLBOT_DEBUG__ ?? {
                wsInvalidMessages: [],
                wsInvalidMessageDetails: [],
              };
              debugWindow.__DWELLBOT_DEBUG__ = {
                wsInvalidMessages: [...existing.wsInvalidMessages, rawPayload].slice(-25),
                wsInvalidMessageDetails: [
                  ...existing.wsInvalidMessageDetails,
                  {
                    raw: rawPayload,
                    issues: parsed.err.issues,
                    code: parsed.err.code,
                    context: parsed.err.context,
                    attemptStartIssues,
                    rawType,
                    decodedRaw,
                    decodedParseError,
                    decodedAttemptStartIssues,
                  },
                ].slice(-25),
              };
            }
            addWsBreadcrumb({
              event: 'parse_failed',
              surface: 'control',
              direction: 'in',
              handler: parsed.err.context?.handler,
              ws_type: parsed.err.context?.type,
              session_id: sessionId,
              robot_id: robotId,
            });
            logWarn('ws.message.invalid', 'invalid message', {
              contract: {
                source: parsed.err.context?.source,
                code: parsed.err.code,
                handler: parsed.err.context?.handler,
                type: parsed.err.context?.type,
              },
              issues: parsed.err.issues,
              attemptStartIssues,
              rawType,
              decodedParseError,
              raw: WS_DEBUG ? rawPayload : undefined,
            });
            return;
          }
          const data = parsed.value;
          if (WS_DEBUG && (!('type' in data) || data.type !== 'teleop_status' || WS_STATUS_LOG)) {
            logInfo('ws.message', 'message', { data });
          }
          if ('type' in data && data.type === 'auth' && 'status' in data && data.status === 'ok') {
            addWsBreadcrumb({ event: 'auth_ok', surface: 'control', session_id: sessionId, robot_id: robotId });
            setConnected(true);
            setError(null);
            reconnectAttemptRef.current = 0;
            clearReconnectTimeout();
            startHeartbeatInterval();
            sendHeartbeat();
            return;
          }
          if ('type' in data && data.type === 'ws_pong') {
            addWsBreadcrumb({ event: 'message_in', surface: 'control', direction: 'in', ws_type: data.type });
            return;
          }
          if ('error' in data && typeof data.error === 'string') {
            setError(data.error);
            if (LEASE_RECOVERY_ERRORS.has(data.error)) {
              logWarn('ws.lease.recovery_needed', 'lease recovery required', {
                reason: data.error,
                sessionId,
                robotId,
                hasLeaseToken: Boolean(leaseToken),
              });
              onInvalidLease?.();
            }
            return;
          }
          setLastMessage(data);
          listenersRef.current.forEach((listener) => listener(data));
        } catch {
          addWsBreadcrumb({ event: 'parse_failed', surface: 'control', direction: 'in' });
          setError('Failed to parse message');
        }
      });

      socket.addEventListener('close', (event) => {
        addWsBreadcrumb({ event: 'close', surface: 'control', close_code: event.code, close_reason: event.reason });
        if (canceled) return;
        setConnected(false);
        clearHeartbeatInterval();
        logWarn('ws.closed', 'closed');
        scheduleReconnect();
      });

      socket.addEventListener('error', () => {
        addWsBreadcrumb({ event: 'error', surface: 'control' });
        if (canceled) return;
        setError('WebSocket error');
        setConnected(false);
        clearHeartbeatInterval();
        logError('ws.error', 'error');
        scheduleReconnect();
      });
    };

    reconnectAttemptRef.current = 0;
    clearReconnectTimeout();
    setConnected(false);
    connectTimeout = setTimeout(connect, WS_CONNECT_DELAY_MS);

    return () => {
      canceled = true;
      setConnected(false);
      lastMotionBufferedAmountBytesRef.current = 0;
      motionBackpressureSkipCountRef.current = 0;
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      clearReconnectTimeout();
      clearHeartbeatInterval();
      if (WS_DEBUG) {
        logInfo('ws.cleanup', 'cleanup');
      }
      socket?.close();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [
    enabled,
    sessionId,
    robotId,
    leaseToken,
    onInvalidLease,
    wsUrl,
    getToken,
    isLoaded,
    isSignedIn,
    traceId,
  ]);

  const sendMessage = useCallback((message: ControlClientMessage) => {
    const activeSocket = socketRef.current;
    const isSocketUnavailable = !activeSocket || activeSocket.readyState !== WebSocket.OPEN;
    if (isSocketUnavailable) {
      setError('WebSocket is not connected');
      logWarn('ws.send.failed', 'send failed: not connected', { message });
      return false;
    }
    const commandKind = getGamepadCommandKind(message);
    const isMotionCommand = commandKind === 'motion';
    if (WS_DEBUG) {
      logInfo('ws.send', 'send', { message });
    }
    if (isMotionCommand) {
      const previousBufferedAmountBytes = lastMotionBufferedAmountBytesRef.current;
      const bufferedAmountBeforeSend = activeSocket.bufferedAmount;
      logMotionBufferedAmountThresholds(
        previousBufferedAmountBytes,
        bufferedAmountBeforeSend
      );
      lastMotionBufferedAmountBytesRef.current = bufferedAmountBeforeSend;

      const isOverBufferedAmountLimit =
        bufferedAmountBeforeSend >= MOTION_BUFFERED_AMOUNT_LIMIT_BYTES;
      if (isOverBufferedAmountLimit) {
        motionBackpressureSkipCountRef.current += 1;
        const motionBackpressureSkipCount =
          motionBackpressureSkipCountRef.current;
        const shouldLogBackpressureSkip = shouldLogSample(
          motionBackpressureSkipCount,
          MOTION_DIAGNOSTIC_SAMPLE_RATE
        );
        if (shouldLogBackpressureSkip) {
          logWarn(
            'teleop.motion.backpressure_skip',
            'skipped motion send because control socket is backpressured',
            {
              buffered_amount_bytes: bufferedAmountBeforeSend,
              buffered_amount_limit_bytes:
                MOTION_BUFFERED_AMOUNT_LIMIT_BYTES,
              robot_id: robotId ?? null,
              sample_rate: MOTION_DIAGNOSTIC_SAMPLE_RATE,
              session_id: sessionId,
            }
          );
        }
        return false;
      }
    }

    activeSocket.send(JSON.stringify(message));
    addWsBreadcrumb({ event: 'message_out', surface: 'control', direction: 'out', ws_type: message.type });

    if (isMotionCommand) {
      const bufferedAmountBeforeSend = lastMotionBufferedAmountBytesRef.current;
      const bufferedAmountAfterSend = activeSocket.bufferedAmount;
      logMotionBufferedAmountThresholds(
        bufferedAmountBeforeSend,
        bufferedAmountAfterSend
      );
      lastMotionBufferedAmountBytesRef.current = bufferedAmountAfterSend;
    }

    return true;
  }, [logMotionBufferedAmountThresholds, robotId, sessionId]);

  const addMessageListener = useCallback((listener: (message: ControlMessage) => void) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const value = useMemo(
    () => ({
      connected,
      sendMessage,
      addMessageListener,
      lastMessage,
      error,
    }),
    [connected, sendMessage, addMessageListener, lastMessage, error]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
